<template lang="pug">
.holder(
  :class="[hoverEnabled ? 'hover-enabled' : 'hover-disabled', { 'deals-layout': dealsLayout }]"
  data-allow-mismatch="class"
  :data-cnstrc-item-id="product.productId"
  :data-cnstrc-item-name="product.name"
  :data-cnstrc-item-variation-id="cnstrcVariationId"
  :data-cnstrc-item-price="cnstrcItemPrice"
  :data-cnstrc-sl-campaign-id="product.sl_campaign_id"
  :data-cnstrc-sl-campaign-owner="product.sl_campaign_owner"
)
  .height
    .img-space
    .content
  rogue-link.hover-card(:to="active.url" :productId="linkProductId"  @mouseleave="active = defaultActive")
    .main-img(@mouseover="hovered = true" @mouseleave="hovered = false")
      StaticImage.img(:src="thumbnail" :width="361" :height="361" :alt="active.name" :eager="preload || undefined" :fetchPriority="fetchPriority")
      .snipe-img
        HotDealBadge.hot-deal-snipe(v-if="activePromos[promoId] && promoStatus !== 'missed' && !showUpcoming" :label="__('Hot Deal')")
        StaticImage.img.hundo(v-else-if="isHundoProduct" src="/UI/logos/Hundo_Category_Snipe_s7bwil.svg" :width="224" :height="44" :alt="__('Hundo Pricing')")
    .content
      .related(v-if="product.related && product.related.length")
        .slides.custom-scroll
          StaticImage.related-thumb( @mouseover="active = product" :src="product.thumbnail || product.altImage" :width="50" :alt="product.name" lazy :eager="preload || undefined" )
          StaticImage.related-thumb(
            v-for="rel in product.related"
            :alt="rel.name"
            :key="rel.url || `${product.productId}-${rel.sku || rel.productId}`"
            @mouseover="active = rel"
            lazy
            :src="rel.related || rel.thumbnail"
            :width="50"
          )
      .title {{active.name}}
      hotdeal(v-if="activePromos[barPromoId] && !showUpcoming" :productId="barPromoId")
      template(v-if="!active.hidePrice")
        Price(v-if="!active.quoted" :product="active" theme="category" :show-excl-vat="false")
        .quoted-price(v-else) {{ __('Quote Available') }}
      .star-hold
        product-stars.stars(:product="active" v-if="active.sku" )
      .variations( v-if="product.related && product.related.length && !compare && hoverEnabled" )
        | {{__('{count} ' + __(product.relatedLabelName || 'colors'), {count: product.related.length + 1})}}
      .compare-toggle(v-if="compare" @click.stop="" )
        Checkbox(
          :modelValue="compare.checked"
          :disabled='compare.disabled'
          name="Compare"
          :id="`compare-'${product.productId}`"
          @update:modelValue="compareToggle()"
        )
          .compare-text.view-compare(
            v-if="compare.checked && compare.count > 1"
            @click.stop.prevent="viewCompare()"
          ) {{ __('See Comparison') }}
          .compare-text(v-else) {{ __('Compare') }}
    .overlay(v-if="showOverlay")
      .overlay-body
        span.deal(v-if="showMissedDeal") MISSED DEAL
        span.deal(v-if="showUpcoming") UPCOMING
        span.challenge(v-if="showChallengeStatus") {{ challengeStatus }}
</template>
<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, onServerPrefetch, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useCategoryStore } from '@/js/stores/categoryStore'
import { usePromoStore } from '@/js/stores/promoStore'
import productStars from '@/js/components/product-stars.vue'
import { useRogue } from '@/js/services/Rogue'
import { shownPromoPrice } from '@/js/helpers/promo-price'

// Async so the badge loads in its own chunk rather than being bundled with another importer's chunk group
const HotDealBadge = defineAsyncComponent(() => import('sisu/components/core/HotDealBadge.vue'))

type HoverCardProduct = {
  relatedLabelName?: string
  productId : number,
  variationKey?: string,
  thumbnail? : string,
  price? : number,
  quoted?: boolean
  attributes: { tax_class_id?: string, manufacturer?: string },
  related?: {
    productId : number,
    variationKey?: string,
    related?: string,
    color?: string,
    thumbnail : string,
    altImage? : string,
    url?: string,
    sku?: string,
    name?: string,
    price?: number,
    special_price?: number,
  }[],
  brand?: string,
  altImage?: string,
  special_price?: number | string,
  name: string,
  challenge_status?: string,
  url: string,
  sku: string,
  hidePrice?: boolean,
  sl_campaign_id?: string,
  sl_campaign_owner?: string,
}

type ActiveProduct = {
  productId: number,
  childId?: number,
  variationKey?: string,
  thumbnail?: string,
  altImage?: string,
  name?: string,
  url?: string,
  quoted?: boolean,
  sku?: string,
  price?: number,
  special_price?: string | number,
  attributes?: { tax_class_id?: string, manufacturer?: string },
  hidePrice?: boolean,
  hasRedirect?: boolean,
}

const props = withDefaults(defineProps<{
  product: HoverCardProduct
  preload?: boolean
  category?: Record<string, any> | null
  fetchPriority?: 'high' | 'low' | 'auto'
  dealsLayout?: boolean
}>(), {
  preload       : false,
  category      : null,
  fetchPriority : 'auto',
  dealsLayout   : false,
})

const active = ref<ActiveProduct>(props.product)
const hovered = ref(false)
// The link's productId hint is only set in the browser: hydration keeps attribute values from the server HTML, so when
// the browser orders cards differently from cached/server HTML, a server-rendered hint stays on a card that has since
// moved, and internal-link.ts would open the wrong product on click.
const isMounted = ref(false)
onMounted(() => { isMounted.value = true })
const linkProductId = computed(() => (isMounted.value && !active.value.hasRedirect ? active.value.productId : ''))

const categoryStore = useCategoryStore()
const promoStore = usePromoStore()
const rogue = useRogue()
const route = useRoute()
const router = useRouter()

const cnstrcVariationId = computed(() => {
  if (!props.product.related?.length) return undefined
  if (active.value.variationKey) return active.value.variationKey
  if (active.value.childId) return active.value.childId

  return active.value.productId
})

const cnstrcItemPrice = computed(() => {
  const price = active.value.price
  const salePrice = active.value.special_price !== undefined ? Number(active.value.special_price) : undefined

  if (!price) return undefined

  return salePrice && salePrice > 0 && salePrice < price ? salePrice : price
})

const activePromos = computed(() => promoStore.activePromos)
const hundoProductIds = computed(() => promoStore.hundoProductIds)

function promoIdFor({ productId, childId, variationKey }: ActiveProduct) {
  if (!variationKey) return childId ?? productId

  return activePromos.value[productId]?.variations?.some(v => v.key === variationKey) ? productId : parseInt(variationKey, 10)
}

// Same entry loadActive() prices from, so the badge never claims a deal the card's price and link don't have.
const promoId = computed(() => promoIdFor(active.value))

// The bar tracks the card's product as a whole, so hovering a swatch doesn't swing it between children. A hovered
// swatch whose own deal is missed or absent hands over its own id, so the bar hides along with the badge and price.
const barPromoId = computed(() => {
  const hoveredPromo = activePromos.value[promoId.value]
  const cardId = promoIdFor(props.product)

  return hoveredPromo && hoveredPromo.status !== 'missed' && activePromos.value[cardId] ? cardId : promoId.value
})

const isDealsPage = computed(() => route.path.indexOf('/deals') >= 0)
const promoStatus = computed(() => activePromos.value[promoId.value]?.status)

const showMissedDeal = computed(() => isDealsPage.value && promoStatus.value === 'missed')
const showUpcoming = computed(() => isDealsPage.value && promoStatus.value === 'upcoming')

const challengeStatus = computed(() => (props.product.challenge_status === 'Complete' ? 'Challenge Complete' : ''))
const showChallengeStatus = computed(() => ['Complete'].includes(props.product.challenge_status || ''))
const showOverlay = computed(() => showMissedDeal.value || showUpcoming.value || showChallengeStatus.value)

const compare = computed(() => {
  if (!props.category || !props.category.compare || !props.category.compare.length) {
    return false
  }

  const ids = categoryStore.compare[props.category.categoryId] || []
  const checked = ids.includes(props.product.productId)

  return {
    show     : true,
    checked,
    count    : ids.length,
    disabled : ids.length > 3 && !checked,
    data     : {
      categoryId : props.category.categoryId,
      productId  : props.product.productId,
    },
  }
})

const thumbnail = computed(() => {
  // don't show placeholder alternate images
  if (active.value.altImage && active.value.altImage !== '/catalog/product_placeholder.png'
  ) {
    return hovered.value ? active.value.altImage : active.value.thumbnail
  }

  return active.value.thumbnail
})

const isHundoProduct = computed(() => {
  const filteredHundo = hundoProductIds.value.filter(id => id === `${active.value.productId}`)

  return filteredHundo.length > 0
})

const hoverEnabled = computed (() => {
  return !rogue.featureFlags.isOn('hover_card_disabled')
})

// Resolve which variant to show when no swatch is being hovered. Prefer the variant
// Constructor matched to the applied facet (surfaced as `variation_id` → variationKey),
// then fall back to matching the applied color facet value against related colors.
function resolveDefaultActive(): ActiveProduct {
  const related = props.product.related
  if (!related?.length) return props.product

  // Constructor pushes the matched variation as `variation_id` (→ variationKey) when a
  // variation facet like color is applied. Trust it as the source of truth: find the
  // matching related variant, and if it isn't in `related` the match is the parent itself
  // (its duplicate thumbnail is filtered out of `related` in cnstrc/util), so show the
  // parent. Do NOT fall through to color-string matching here — related[].color can be
  // unreliable (e.g. bundles report the same color for every variant).
  const matchedKey = props.product.variationKey
  if (matchedKey) {
    const byVariation = related.find(r => r.variationKey && String(r.variationKey) === String(matchedKey))

    return byVariation || props.product
  }

  // No Constructor variation id (e.g. legacy/RFK): fall back to the applied color facet
  // value. Constructor uses `color`; legacy/RFK URLs may use `colorstandard` / `color_standard`.
  const colorParam = route.query?.color ?? route.query?.colorstandard ?? route.query?.color_standard

  if (colorParam) {
    const colors = (Array.isArray(colorParam) ? colorParam : [colorParam])?.map(c => String(c)?.toLowerCase())
    const byColor = related.find(r => r.color != null && colors?.includes(String(r.color).toLowerCase()))

    if (byColor) return byColor
  }

  return props.product
}

// Reactive so a client-side facet click (route.query change / new product props)
// re-resolves the displayed variant — not just the initial SSR/direct load.
const defaultActive = computed<ActiveProduct>(() => resolveDefaultActive())
active.value = defaultActive.value
watch(defaultActive, (val) => { active.value = val })

async function loadActive() {
  // All of these except promo price should default to the values from reflektion.
  // This is a fallback in case the reflection fields are missing.
  active.value.name = active.value.name || props.product.name
  active.value.sku = active.value.sku || props.product.sku
  active.value.url = active.value.url || props.product.url
  // needed for attributes.tax_class_id in price component
  active.value.attributes = active.value.attributes || props.product.attributes

  if (!active.value.price) {
    active.value.price = active.value.price || props.product.price
    active.value.special_price = props.product.special_price
  }
  // Only a hot deal price the shared rule allows: active always, missed only when this card shows as a Missed Deal (on
  // the deals page), upcoming never. The price is written into the product the card shows, which the deals page sorts
  // on, so this must match deals.vue's lowestPrice() or the browser re-sorts the server-rendered cards.
  const promo = active.value.variationKey
    ? activePromos.value[active.value.productId]?.variations?.find(v => v.key === active.value.variationKey) || activePromos.value[parseInt(active.value.variationKey, 10)]
    : activePromos.value[active.value.childId ?? active.value.productId]
  const promoPrice = shownPromoPrice(promo, !!showMissedDeal.value)

  if (promoPrice !== undefined) {
    active.value.price = active.value.price || promoPrice
    active.value.special_price = Math.min(promoPrice, (active.value.special_price || active.value.price) as number)
  }
}

onServerPrefetch(async () => {
  await loadActive()
})

// Immediate, so a card rendered only in the browser (in-app navigation) applies its hot deal price too - otherwise a
// Missed Deal card shows the regular price while the deals page sorts it by the missed price. After a server render this
// writes the same value the hydrated product already has, so nothing changes on hydration.
watch(active, loadActive, { immediate: true })

function viewCompare() {
  if (!props.category) return

  const ids = categoryStore.compare[props.category.categoryId] || []
  router.push({
    path  : `/compare${props.category.url}`,
    query : {
      categoryId : props.category.categoryId,
      ids        : ids.join(','),
    },
  })
}

function compareToggle() {
  if (compare.value) {
    categoryStore.toggleCompare(compare.value.data.categoryId, compare.value.data.productId)
  }
}
</script>
<style lang="scss" scoped>
.holder {
  position: relative;
  width: 100%;
}

.hover-card {
  display: block;
  padding: $space-xxs;
  position: absolute;
  width: auto;
  cursor: pointer;
  text-decoration: none;
  top: 0;
  left: 0;
  right: 0;

  .related {
    display: none;
  }
}

.holder.hover-enabled {

  @include all-desktop() {
    .hover-card:hover {
      border: $border-width-thin solid $color-gray-light;
      border-radius: 2px;
      height: auto;
      background-color: white;
      z-index: 1;
      left: -9px;
      right: -9px;
      top: -9px;
      padding: $space-s;

      .related {
        display: block;
      }
    }
  }
}

.compare-text {
  color: $color-black-light;
  font-size: $size-xxs;

  .checkbox.disabled & {
    opacity: $opacity-disabled;
  }
}

.title {
  font-size: $size-xs;
  color: $color-black;
  display: block;
  min-height: $space-xl;
  margin-top: $space-xxs;
  text-decoration: none;
}

.quoted-price {
  font-size: $size-xs;
  color: $color-black;
  font-weight: bold;
}

.product-price {
  font-size: $size-xs;
  color: $color-black;

  .price {
    font-weight: bold;
  }

  .starting-at {
    font-size: $size-xxs;
  }

  .old-price, .price-including-tax {
    font-size: $size-xxs;
    color: $color-gray-dark;
  }

  .old-price, .price-including-tax .price {
    margin-left: $space-xxxs;
  }

  .price-including-tax {
    margin-top: $space-xxs;
  }

  .old-price {
    text-decoration: line-through;
  }
}

.star-hold {
  margin-top: $space-xxxs;
  margin-bottom: $space-xxxs;
  height: 24px;
}

.variations {
  font-size: $size-xxs;
  margin-top: $space-s;
  color: $color-gray-dark;
}

.content {
  min-height: 144px;
  padding-top: $space-xxs;
  margin-bottom: $space-xs;
}

.main-img {
  cursor: pointer;
  position: relative;
  max-width: 361px;
  max-height: 361px;
}

.img-space {
  height: 0;
  padding-top: 100%;
}

.related {
  overflow: hidden;
}

.slides {
  display: flex;
  overflow-x: auto;

  &::-webkit-scrollbar:horizontal {
    height: 4px;
    background: transparent;
  }
}

.snipe-img {
  .img {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50%;

    &.hundo {
      width: 62%;
    }
  }

  // Sized and inset to match the old snipe image, whose artwork had its padding built in
  .hot-deal-snipe {
    --hot-deal-badge-bg: #{$color-gray-x-light};
    position: absolute;
    bottom: 2.5%;
    left: 2%;
    width: 46%;
  }
}

.related-thumb {
  margin-right: 2px;
  width: 50px;
  min-width: 50px;
  flex-shrink: 0;
}

.overlay {
  position:absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255,255,255, .5);
  pointer-events: none;

  .overlay-body {
    position: absolute;
    width: 100%;
    top: 35%;
    text-align: center;

    span {
      background-color: $color-white;
      padding: 5px 5px;
      font-weight: bold;
      color: $color-black;
    }
  }
}

.holder.hover-disabled {
  padding: $space-xxs;

  width: 50%;

    @media screen and (min-width: 480px) {
      width: 33.333%;
    }

    @media screen and (min-width: 960px) {
      width: 25%;
    }

  .height{
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    pointer-events: none;
  }

  .hover-card {
    position: inherit;
    padding: 0;

    .related{
      display: block;
    }
  }

  .content{
    min-height: auto;
  }

  .custom-scroll{
    padding-bottom: $space-xxs;
  }
}

.holder.deals-layout {
  .overlay-body {
    top: 0;
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.hover-disabled .hover-card:not(:has(.related)) > .content {
    padding-top: calc(#{$space-xxs} + 50px + #{$space-xxs});
  }
}

</style>

<style lang="scss" scoped target="app">
.platform-ios, .platform-android {
  .compare-toggle .checkbox {
    padding-right: 0;
    padding-left: 0;
  }

  .holder .height > .content {
    margin-bottom: $size-xxl;
  }
}
</style>
